import { z } from "zod";

/**
 * The website Joogoos makes, for a business that has none
 * ([28](../../../docs/28-the-website-joogoos-makes.md), [ADR 0047](../../../docs/adr/0047-a-business-with-no-website-gets-one-built-by-joogoos-and-hosted-in-the-customers-own-cloudflare-account.md)).
 *
 * **The model designs the page; code decides what of it may be published** (ADR 0047 §4 as amended 2026-09-25).
 * `write_site` answers with the page's body as HTML and its stylesheet, and `sanitizeWebsite` in `@joogoos/social`
 * keeps only an allowlist of both — no script, no inline style or handler, no link but a phone, an email or a place
 * on the page, no picture but the owner's own — then checks what is left before anybody sees it.
 */

/**
 * What `write_site` answers. No length limits in the schema: a structured output that runs over one fails the whole
 * call, and a failed call is paid for. The sanitiser holds the limits instead.
 */
export const WebsitePage = z.object({
  /** The two-letter code of the language it is written in, for `<html lang>`. Checked, and `en` when it is not one. */
  lang: z.string(),
  /** The `<title>` and the line a search result shows. */
  metaTitle: z.string(),
  metaDescription: z.string(),
  /** At most two Google Fonts families by name, e.g. "Fraunces". Code builds the link; anything else is dropped. */
  fonts: z.array(z.string()),
  /** What goes inside `<body>`. Pictures are `src="img:logo"` or `src="img:1"`…, never an address. */
  html: z.string(),
  /** The whole stylesheet. Served as a file, never inline. */
  css: z.string(),
});
export type WebsitePage = z.infer<typeof WebsitePage>;

/** A version as it is kept: the design, and which of the owner's pictures each `img:` reference meant when it was written. */
export type WebsiteVersionPage = WebsitePage & { images: Record<string, string> };

export const WEBSITE_LIMITS = { html: 80_000, css: 60_000, metaTitle: 70, metaDescription: 170, fonts: 2 } as const;

// ---------------------------------------------------------------------------------------------------------------
// Cloudflare: where the page lives. The account is the customer's; Joogoos holds one scoped token.
// ---------------------------------------------------------------------------------------------------------------

/**
 * What the token may do, as Cloudflare's template link names it
 * ([template URLs](https://developers.cloudflare.com/fundamentals/api/how-to/account-owned-token-template/)).
 *
 * Deploying a Worker's static files (`workers_scripts`), putting it on the domain the owner bought (`workers_routes`,
 * `dns`), finding that domain (`zone`), and reading the account's name so the screen can say whose it is
 * (`account_settings`). Nothing to do with billing, members, email or anything else in the account.
 */
export const CLOUDFLARE_TOKEN_PERMISSIONS = [
  { key: "account_settings", type: "read" },
  { key: "workers_scripts", type: "edit" },
  { key: "workers_routes", type: "edit" },
  { key: "zone", type: "read" },
  { key: "dns", type: "edit" },
] as const;

export const CLOUDFLARE_LINKS = {
  signUp: "https://dash.cloudflare.com/sign-up",
  /** Cloudflare's own registrar, at cost: the domain is in the same account, so the token can reach it. */
  buyDomain: "https://dash.cloudflare.com/?to=/:account/domains/register",
  token: `https://dash.cloudflare.com/profile/api-tokens?permissionGroupKeys=${encodeURIComponent(JSON.stringify(CLOUDFLARE_TOKEN_PERMISSIONS))}&accountId=*&zoneId=all&name=${encodeURIComponent("Joogoos website")}`,
} as const;

/**
 * Whether a pasted string could be an API token at all. A **global API key** is 37 hexadecimal characters and
 * cannot be scoped, so it is refused by shape before it is stored — the one mistake here that would hand Joogoos the
 * whole account.
 */
export function tokenShape(raw: string): "token" | "global_key" | "not_a_token" {
  const t = raw.trim();
  if (/^[0-9a-f]{37}$/i.test(t)) return "global_key";
  return /^[A-Za-z0-9_-]{30,200}$/.test(t) ? "token" : "not_a_token";
}

// ---------------------------------------------------------------------------------------------------------------
// The API.
// ---------------------------------------------------------------------------------------------------------------

export const WebsiteToken = z.object({ token: z.string().min(1).max(400) });
/** The domain to put the site on, by Cloudflare's zone id; null goes back to the free `workers.dev` address. */
export const WebsiteDomain = z.object({ zoneId: z.string().regex(/^[0-9a-f]{32}$/).nullable() });
/** What the owner wants changed, in their words. Empty is "write it from the Business page". */
export const WebsiteWrite = z.object({ ask: z.string().max(1_000).default("") });
/** The two ways in, printed on the page as they are typed here. Empty leaves a button off. */
export const WebsiteContact = z.object({
  phone: z.string().max(40).regex(/^[+0-9 ()-]*$/, "a phone number is digits, spaces and + ( ) -"),
  email: z.union([z.literal(""), z.email().max(200)]),
});

export type WebsiteStatus = "none" | "draft" | "publishing" | "live" | "failed";

export interface WebsiteVersionView {
  id: string;
  /** What the owner asked for, or empty for a first page written from the Business page. */
  ask: string;
  createdAt: string;
  metaTitle: string;
}

/** `GET /v1/website` — the whole screen. */
export interface WebsiteResponse {
  /** False when this box cannot seal a token: the screen says so rather than offering a button that fails. */
  available: boolean;
  /**
   * The address the Business page already has, when it is not one Joogoos made (ADR 0040). Then there is nothing to
   * build: the screen says so, and the API refuses one (28 §1).
   */
  otherWebsite: string | null;
  cloudflare: {
    accountName: string;
    /** The last four characters of the token, so somebody can tell which one Joogoos holds. */
    tokenHint: string;
    /** The domains in that account, to put the site on. Read from Cloudflare each time the screen opens. */
    zones: { id: string; name: string }[];
    /** Set when the zones could not be read — a token without that permission, or Cloudflare being down. */
    zonesError: string | null;
  } | null;
  /** The chosen domain, or null for the free address. */
  domain: { zoneId: string; name: string } | null;
  contact: { phone: string; email: string };
  status: WebsiteStatus;
  /** Where the site answers, with the scheme. Null until the first deployment. */
  address: string | null;
  lastDeployAt: string | null;
  lastError: string | null;
  current: WebsiteVersionView | null;
  /** Which version is on the site now, so "Publish" can say whether there is anything new to send. */
  publishedVersionId: string | null;
  /** Earlier versions, newest first, without their pages. */
  history: { id: string; ask: string; createdAt: string }[];
  /** What designing costs, in dollars, from the operation's own estimate (13). */
  price: string;
  /** Whether the page is being designed right now (a job, one to three minutes), so two presses are not two bills. */
  writing: boolean;
  /** Why the last design did not arrive, in words for the owner. Cleared by the next one. */
  writeError: string | null;
  links: typeof CLOUDFLARE_LINKS;
}
